import { ExperimentDesignFull } from '../../../lib/api/experimentDesignApi';
import { Experiment } from '../../../lib/api/experimentQueueApi';

/**
 * Experiment Design Utilities
 * 
 * Sample Size Calculation Logic:
 * - Leaf designs (no children): Use their explicit sample_size value
 * - Parent designs (with children): sample_size is always null, calculated total is displayed
 * - Calculated total = sum of all leaf descendant sample_size values
 * - This prevents double-counting and ensures data consistency
 */

const chakraColorPalette = [
    'gray', 'red', 'orange', 'yellow', 'green', 'teal', 'blue', 'cyan', 'purple', 'pink'
];

// Helper function to get inherited value from parent hierarchy
export const getInheritedValue = (design: ExperimentDesignFull, field: keyof ExperimentDesignFull, allDesigns: ExperimentDesignFull[]): any => {
    // Find the design in the flat list to get its hierarchy position
    const findParent = (designId: number): ExperimentDesignFull | null => {
        const current = allDesigns.find(d => d.experiment_design_id === designId);
        if (!current || !current.parent_experiment_design_id) return null;
        return allDesigns.find(d => d.experiment_design_id === current.parent_experiment_design_id) || null;
    };

    let currentParent = findParent(design.experiment_design_id);
    while (currentParent) {
        const value = currentParent[field];
        // Check if value exists - handle both primitive types and arrays
        if (value !== null && value !== undefined && value !== '') {
            // For arrays, check if they have content
            if (Array.isArray(value)) {
                if (value.length > 0) {
                    return { value, source: currentParent };
                }
            } else {
                return { value, source: currentParent };
            }
        }
        currentParent = findParent(currentParent.experiment_design_id);
    }
    return null;
};

// Helper function to determine if a field has an explicit value or is using inheritance
export const hasExplicitValue = (design: ExperimentDesignFull, field: keyof ExperimentDesignFull): boolean => {
    const value = design[field];

    // Handle null, undefined, or empty string
    if (value === null || value === undefined || value === '') {
        return false;
    }

    // Handle arrays - they need to have content to be considered explicit
    if (Array.isArray(value)) {
        return value.length > 0;
    }

    return true;
};

// Helper function to get effective value (explicit or inherited)
export const getEffectiveValue = (design: ExperimentDesignFull, field: keyof ExperimentDesignFull, allDesigns: ExperimentDesignFull[]): any => {
    if (hasExplicitValue(design, field)) {
        return design[field];
    }

    const inherited = getInheritedValue(design, field, allDesigns);
    return inherited ? inherited.value : null;
};

// Helper function to check if interventions are required (explicit or inherited)
export const getEffectiveInterventionRequired = (design: ExperimentDesignFull, allDesigns: ExperimentDesignFull[]): boolean => {
    // If explicitly set (true or false), use that value
    if (design.intervention_required !== null && design.intervention_required !== undefined) {
        return design.intervention_required;
    }

    // If null/undefined, inherit from parent
    const inherited = getInheritedValue(design, 'intervention_required', allDesigns);
    return inherited ? inherited.value : false;
};

// Helper function to check if additional samples are required (explicit or inherited)
export const getEffectiveAdditionalSamplesRequired = (design: ExperimentDesignFull, allDesigns: ExperimentDesignFull[]): boolean => {
    // If explicitly set (true or false), use that value
    if (design.additional_samples_required !== null && design.additional_samples_required !== undefined) {
        return design.additional_samples_required;
    }

    // If null/undefined, inherit from parent
    const inherited = getInheritedValue(design, 'additional_samples_required', allDesigns);
    return inherited ? inherited.value : false;
};

// Helper function to check if deviations are required (explicit or inherited)
export const getEffectiveDeviationsRequired = (design: ExperimentDesignFull, allDesigns: ExperimentDesignFull[]): boolean => {
    // If explicitly set (true or false), use that value
    if (design.deviations_required !== null && design.deviations_required !== undefined) {
        return design.deviations_required;
    }

    // If null/undefined, inherit from parent
    const inherited = getInheritedValue(design, 'deviations_required', allDesigns);
    return inherited ? inherited.value : false;
};

// Helper function to determine if a design is a leaf node (has no children)
export const isLeafNode = (design: ExperimentDesignFull): boolean => {
    return !design.children || design.children.length === 0;
};

// Helper function to calculate total sample size from children
export const calculateTotalSampleSize = (design: ExperimentDesignFull): number | null => {
    if (!design.children || design.children.length === 0) {
        return design.sample_size || null;
    }

    let total = 0;
    let hasAnyChildWithSampleSize = false;

    for (const child of design.children) {
        const childTotal = calculateTotalSampleSize(child);
        if (childTotal !== null) {
            total += childTotal;
            hasAnyChildWithSampleSize = true;
        }
    }

    return hasAnyChildWithSampleSize ? total : null;
};

// Helper function to get missing sample size warnings for leaf nodes
export const getMissingSampleSizeLeafNodes = (design: ExperimentDesignFull): ExperimentDesignFull[] => {
    const missingNodes: ExperimentDesignFull[] = [];

    if (isLeafNode(design)) {
        if (!design.sample_size) {
            missingNodes.push(design);
        }
    } else if (design.children) {
        design.children.forEach(child => {
            missingNodes.push(...getMissingSampleSizeLeafNodes(child));
        });
    }

    return missingNodes;
};

export const getStatusBadgeColor = (status: string | null) => {
    const colorMap: Record<string, string> = {
        'Planning': 'gray',
        'In Progress': 'blue',
        'Completed': 'green',
        'On Hold': 'yellow',
        'Cancelled': 'red'
    };
    return colorMap[status || ''] || 'gray';
};

export const getBadgeColor = (id: number | string | null | undefined) => {
    // convert string to number using simple hash if it's a string
    const colorList = chakraColorPalette;
    if (id === null || id === undefined) return 'gray';

    let idNumber: number;
    if (typeof id === 'string') {
        let hash = 0;
        for (let i = 0; i < id.length; i++) {
            hash = (hash << 5) - hash + id.charCodeAt(i);
        }
        idNumber = Math.abs(hash);
    } else {
        idNumber = id;
    }

    const color = colorList[idNumber % colorList.length] || 'gray';
    return color;
};

// Helper function to determine if a design has valid sample sizes for queue operations
export const hasValidSampleSize = (design: ExperimentDesignFull): boolean => {
    const totalSampleSize = calculateTotalSampleSize(design);
    const missingLeafNodes = getMissingSampleSizeLeafNodes(design);

    // Valid if:
    // 1. There's a total sample size (meaning at least one leaf has a sample size)
    // 2. No leaf nodes are missing sample sizes
    return totalSampleSize !== null && missingLeafNodes.length === 0;
};

/**
 * Helper function to calculate the total number of experiments with a calendar slot (perfusion info) for a design
 * This includes experiments from child designs
 * @param design - The experiment design
 * @param allDesigns - All experiment designs in the hierarchy
 * @returns The total number of experiments that have perfusion information (any status)
 */
export const getExperimentsWithCalendarSlotCount = (
    design: ExperimentDesignFull,
    allDesigns: ExperimentDesignFull[],
    perfusionStatusCounts: Record<number, Record<string, number>>
): number => {
    let count = 0;

    // If the current design is a leaf, check its own perfusion status
    if (isLeafNode(design)) {
        // We consider an experiment to have a "calendar slot" if it has any perfusion info
        // This implicitly means it's been scheduled or has run
        const designPerfusionCounts = getTotalPerfusionRunsForDesign(design.experiment_design_id, perfusionStatusCounts);
        count += designPerfusionCounts;
    } else {
        // If it's a parent, recursively sum from children
        if (design.children) {
            design.children.forEach(child => {
                count += getExperimentsWithCalendarSlotCount(child, allDesigns, perfusionStatusCounts);
            });
        }
    }
    return count;
};

// Helper function to calculate remaining experiments that can be pushed to queue
export const calculateRemainingExperiments = (design: ExperimentDesignFull, queuedCount: number, experimentsWithCalendarSlotCount: number): number => {
    const totalSampleSize = calculateTotalSampleSize(design);
    if (totalSampleSize === null) return 0;

    // Remaining = Total Sample Size - Experiments with calendar slot - Queued Count
    return Math.max(0, totalSampleSize - experimentsWithCalendarSlotCount - queuedCount);
};

// Helper function to determine if push to queue button should be disabled
export const isPushToQueueDisabled = (design: ExperimentDesignFull, queuedCount: number, experimentsWithCalendarSlotCount: number): boolean => {
    // Disabled if:
    // 1. Design doesn't have valid sample sizes
    // 2. All experiments for this design are already in queue or have calendar slots
    if (!hasValidSampleSize(design)) return true;

    const remaining = calculateRemainingExperiments(design, queuedCount, experimentsWithCalendarSlotCount);
    return remaining <= 0;
};

// Helper function to get the text for the push to queue button
export const getPushToQueueButtonText = (design: ExperimentDesignFull, queuedCount: number, experimentsWithCalendarSlotCount: number): string => {
    // If the button would be disabled, don't show any text
    if (isPushToQueueDisabled(design, queuedCount, experimentsWithCalendarSlotCount)) {
        return '';
    }

    const remainingCount = calculateRemainingExperiments(design, queuedCount, experimentsWithCalendarSlotCount);
    if (remainingCount <= 0) {
        return '';
    }
    return String(remainingCount);
};

// Helper function to get push to queue tooltip text
export const getPushToQueueTooltipText = (design: ExperimentDesignFull, queuedCount: number, experimentsWithCalendarSlotCount: number): string => {
    if (!hasValidSampleSize(design)) {
        return "Cannot push to queue: Missing sample sizes in leaf designs";
    }

    const totalSampleSize = calculateTotalSampleSize(design);
    const remaining = calculateRemainingExperiments(design, queuedCount, experimentsWithCalendarSlotCount);

    // New total for display in tooltip
    const totalForDisplay = totalSampleSize !== null ? totalSampleSize - experimentsWithCalendarSlotCount : null;

    if (remaining <= 0) {
        if (totalForDisplay !== null) {
            return `All eligible experiments (${totalForDisplay}) for this design are already in the queue`;
        }
        return `All experiments for this design are already in the queue`;
    }

    if (queuedCount > 0) {
        return `Push ${remaining} more experiments to queue (${queuedCount}/${totalForDisplay} already queued)`;
    }

    return `Push ${remaining} experiments to queue`;
};

// Helper function to ensure all designs in a list follow the sample_size rules
// Parent designs (those with children) should have sample_size = null
// Leaf designs can have any sample_size value
export const ensureSampleSizeConsistency = (designs: ExperimentDesignFull[]): ExperimentDesignFull[] => {
    return designs.map(design => {
        const hasChildren = designs.some(d => d.parent_experiment_design_id === design.experiment_design_id);
        if (hasChildren && design.sample_size !== null) {
            return { ...design, sample_size: null };
        }
        return design;
    });
};

// Helper function to compute queue counts from experiment queue data
export const computeQueuedExperimentsCounts = (experiments: Experiment[], allDesigns?: ExperimentDesignFull[]): Record<number, number> => {
    const counts: Record<number, number> = {};

    experiments.forEach(experiment => {
        if (experiment.experiment_design_id) {
            const designId = experiment.experiment_design_id;
            counts[designId] = (counts[designId] || 0) + 1;

            // If we have design hierarchy data, also count toward parent designs
            if (allDesigns) {
                const design = allDesigns.find(d => d.experiment_design_id === designId);
                if (design && design.parent_experiment_design_id) {
                    // Walk up the hierarchy and increment counts for all parents
                    let currentParentId: number | null = design.parent_experiment_design_id;
                    while (currentParentId !== null) {
                        counts[currentParentId] = (counts[currentParentId] || 0) + 1;
                        const parentDesign = allDesigns.find(d => d.experiment_design_id === currentParentId);
                        currentParentId = parentDesign?.parent_experiment_design_id || null;
                    }
                }
            }
        }
    });

    return counts;
};

/**
 * Helper function to calculate the total number of runs with perfusion info for a specific experiment design
 * @param designId - The experiment design ID to calculate perfusion runs for
 * @param perfusionStatusCounts - Record of design IDs to their perfusion status counts
 * @returns The total number of runs that have perfusion information (any status)
 */
export const getTotalPerfusionRunsForDesign = (
    designId: number,
    perfusionStatusCounts: Record<number, Record<string, number>>
): number => {
    const statusCounts = perfusionStatusCounts[designId] || {};
    // Exclude 'Queued' status from the count of perfusion runs when determining calendar slots
    return Object.entries(statusCounts).reduce((total, [status, count]) => {
        if (status === 'Queued') {
            return total;
        }
        return total + count;
    }, 0);
};

/**
 * Helper function to get the minimum allowed sample size for a design based on existing perfusion runs
 * @param designId - The experiment design ID
 * @param perfusionStatusCounts - Record of design IDs to their perfusion status counts
 * @param queuedExperimentsCounts - Record of design IDs to their queued experiment counts
 * @returns The minimum sample size (cannot be less than existing perfusion runs)
 */
export const getMinimumSampleSize = (
    designId: number,
    perfusionStatusCounts: Record<number, Record<string, number>>,
    queuedExperimentsCounts: Record<number, number>
): number => {
    const totalPerfusionRuns = getTotalPerfusionRunsForDesign(designId, perfusionStatusCounts);
    const queuedCount = queuedExperimentsCounts[designId] || 0;

    return totalPerfusionRuns + queuedCount;
}; 